<script lang="ts">
	import { page } from '$app/stores';
	import { getSeoImageUrl, type PostMetaData } from '$lib/blog/mdContentUtils';
	import ThemeAwareHighlightJsStyle from '$lib/components/commons/ThemeAwareHighlightJsStyle.svelte';
	import PullRequestUrlInput from '$lib/components/review/PullRequestUrlInput.svelte';
	import type { PlatformBaseUrls } from '$lib/model';
	import { formatDate, formatDateWithoutTime } from '$lib/svelte-utils/utils/dateUtils';
	import { getUrlWithoutSearchParams } from '$lib/svelte-utils/utils/urlUtils';
	import { MetaTags } from 'svelte-meta-tags';

	export let metaData: PostMetaData;
	export let postHtml = '';
	export let baseUrls: PlatformBaseUrls;

	function getFullImageUrl() {
		return getSeoImageUrl({
			title: metaData.title,
			subTitle: 'Codelantis Blog'
		});
	}
</script>

<ThemeAwareHighlightJsStyle />

<MetaTags
	title={metaData.title}
	description={metaData.summary}
	canonical={getUrlWithoutSearchParams($page.url)}
	openGraph={{
		article: {
			publishedTime: new Date(metaData.published).toString(),
			authors: [metaData.author]
		},
		images: [
			{
				url: getFullImageUrl(),
				width: 800
			}
		]
	}}
	twitter={{
		image: getFullImageUrl(),
		title: metaData.title
	}}
/>

<svelte:head
	>{@html `
	<script type="application/ld+json">
		{
		'@context': 'https://schema.org',
		'@type': 'BlogPosting',
		headline: ${metaData.title},
		author: ${metaData.author},
		image: ${getFullImageUrl()},
		articleBody: ${postHtml},
		dateCreated: ${new Date(metaData.published).toString()},
		description: ${metaData.summary}
	}
	</script>`}
</svelte:head>

<article class="gradient-body-bg mx-auto mb-32 max-w-7xl pb-32 lg:mt-24">
	<div
		class="flex flex-col gap-y-4 from-blue-900 to-gray-900 pb-8 dark:from-gray-900 dark:to-transparent dark:text-white"
	>
		<h1
			class="mx-auto mt-16 w-max max-w-full text-balance px-4 py-4 text-xl font-extrabold !leading-tight tracking-tight sm:max-w-3xl sm:px-0 md:mt-16 md:text-5xl"
		>
			{metaData.title}
		</h1>

		<div
			class="mx-auto my-4 flex w-full max-w-full flex-wrap items-center gap-x-4 gap-y-4 px-4 text-sm sm:text-base md:my-8 md:max-w-3xl"
		>
			<div class="flex flex-1 items-center gap-x-4 justify-self-start">
				{#if metaData.author === 'Daniel Veihelmann'}
					<img
						alt=""
						class="size-8 rounded-full object-cover sm:size-12"
						title="Daniel Veihelmann"
						src="/images/daniel-veihelmann.jpg"
					/>
				{/if}
				<div class="flex flex-col">
					<span class="meta-label">Author</span><span class="font-medium">{metaData.author}</span>
				</div>
			</div>
			<div class="self- flex flex-col">
				<span class="meta-label">published</span>
				<span class="font-medium"
					>{formatDate(new Date(metaData.published), 'en-us', 'MONTH_FULL DD/MM/YYY')}</span
				>
				<div>
					{#if metaData.updated}
						<div class="theme-text-secondary mt-2 text-center">
							Latest update: {formatDateWithoutTime(new Date(metaData.updated), 'en-us')}
						</div>
					{/if}
				</div>
			</div>
		</div>
		<div
			class="relative mx-auto mb-12 h-[200px] w-full max-w-3xl overflow-hidden rounded-xl bg-cover bg-center bg-no-repeat object-top md:h-[300px]"
			style="background-image: url('{metaData.coverImageUrl}')"
			title={metaData.coverImageHint ?? ''}
		>
			<div class="h-full w-full bg-gradient-to-br from-gray-900 to-gray-950 opacity-25" />
		</div>
		<div class="padded markdown-container mx-auto max-w-full md:max-w-6xl">
			<div
				class="text-[17px] lg:text-lg [&>p]:py-2 [&>p]:leading-[180%] [&>ul]:leading-relaxed [&_li]:py-2"
			>
				<slot>{@html postHtml}</slot>
			</div>

			<section class="mx-auto max-w-3xl sm:mt-16">
				<div class="mb-3">
					<div class="text-lg font-semibold sm:text-2xl">
						Need to review a complex merge request?
					</div>
				</div>
				<p class="theme-text-secondary mb-3">
					Let's get you some help! Simply paste your URL below:
				</p>
				<PullRequestUrlInput {baseUrls} />
			</section>
		</div>
	</div>
</article>

<style lang="less">
	.meta-label {
		@apply font-mono text-xs font-semibold uppercase tracking-tight text-[#75777a] opacity-75;
	}

	html.dark .meta-label {
		@apply text-[#d1d5db];
	}

	.padded {
		@apply px-8 sm:px-16 md:px-32 lg:px-56;
	}

	:global(html.dark article hr) {
		@apply border-[#444];
	}

	.markdown-container :global(h3) {
		@apply mb-4 font-bold sm:mt-10 sm:text-2xl;
	}

	.markdown-container :global(h2) {
		@apply lg:mb-6 lg:mt-16 lg:text-4xl;
	}

	.markdown-container :global(ol) {
		@apply py-6 pl-4;
	}

	.markdown-container :global(li ul) {
		@apply pl-4;
	}

	:global(html:not(.dark) article *:not(pre) > code) {
		@apply bg-gray-200 text-pink-800;
	}

	:global(html.dark article *:not(pre) > code) {
		@apply bg-slate-900 text-orange-200;
	}
</style>
